import 'reflect-metadata';
import { inject, injectable } from 'inversify';
import { TYPES } from '@Types';
import { unit_option_categories, unit_option_full } from '@Api';
import { UnitOption, UnitOptionCategory, UnitOptionUsePoint } from '@Models';
import { UnitOptionLimitMapper } from './UnitOptionLimitMapper';
import { UnitOptionUsePointMapper } from './UnitOptionUsePointMapper';
import { UnitOptionAvailabilityMapper } from './UnitOptionAvailabilityMapper';
import { UnitOptionChangeModelRuleMapper } from './UnitOptionChangeModelRuleMapper';
import { UnitOptionChangeProfilMapper } from './UnitOptionChangeProfilMapper';
import { UnitOptionOrganisationModeMapper } from './UnitOptionOrganisationModeMapper';
import { UnitOptionMagicItemCategoryMapper } from './UnitOptionMagicItemCategoryMapper';
import { ConstraintMapper } from '@Mapper/ninthAge/constraint.mapper';
import { ConstraintModifierMapper } from '@Mapper/ninthAge/constraint-modifier.mapper';

@injectable()
export class UnitOptionMapper {
  @inject(TYPES.Mapper.UnitOptionAvailabilityMapper)
  private _unitOptionAvailabilityMapper!: UnitOptionAvailabilityMapper;
  @inject(TYPES.UnitOptionLimitMapper)
  private _unitOptionLimitMapper!: UnitOptionLimitMapper;
  @inject(TYPES.UnitOptionUsePointMapper)
  private _unitOptionUsePointMapper!: UnitOptionUsePointMapper;
  @inject(TYPES.Mapper.UnitOptionChangeProfilMapper)
  private _unitOptionChangeProfilMapper!: UnitOptionChangeProfilMapper;
  @inject(TYPES.Mapper.UnitOptionChangeModelRuleMapper)
  private _unitOptionChangeModelRuleMapper!: UnitOptionChangeModelRuleMapper;
  @inject(TYPES.Mapper.UnitOptionOrganisationModeMapper)
  private _unitOptionOrganisationModeMapper!: UnitOptionOrganisationModeMapper;
  @inject(TYPES.Mapper.UnitOptionMagicItemCategoryMapper)
  private _unitOptionMagicItemCategoryMapper!: UnitOptionMagicItemCategoryMapper;
  @inject(TYPES.Mapper.ConstraintMapper)
  private _constraintMapper!: ConstraintMapper;
  @inject(TYPES.Mapper.ConstraintModifierMapper)
  private _constraintModifierMapper!: ConstraintModifierMapper;

  public map(item: unit_option_full): UnitOption {
    const usePoints = this._unitOptionUsePointMapper.map(item.use_points);
    return {
      id: item.id,
      unit_id: item.unit_id,
      parent_id: item.parent_id,
      hidden: item.hidden ?? false,
      position: item.position,
      category: this.mapCategory(item.category),
      is_per_model: item.is_per_model,
      hasChoises: item.has_choices,
      weight: item.weight,
      mount_id: item.mount_id,
      mount_and_carac_points: item.mount_and_carac_points,
      organisation_mode: this._unitOptionOrganisationModeMapper.map(
        item.organisation_mode,
      ),
      is_multiple: item.is_multiple,
      is_required: item.is_required,
      magic_path_id: item.magic_path_id,
      organisation_id: item.organisation_id,
      army_organisation_id: item.army_organisation_id,
      max: item.max,
      name: item.name ?? '',
      points: item.value_points,
      unitBase: item.base,
      unitHeight: item.height,
      use_points: this._unitOptionUsePointMapper.map(item.use_points),
      modelRuleIds: item.model_rule_ids ?? [],
      magic_item_categories: item.magic_item_categories.map((item) =>
        this._unitOptionMagicItemCategoryMapper.map(item),
      ),
      limits: item.unit_option_limits.map((limit) =>
        this._unitOptionLimitMapper.map(item.id, limit),
      ),
      availabilities: item.availabilities.map((availability) =>
        this._unitOptionAvailabilityMapper.map(item.id, availability),
      ),
      availabilityModifiers: item.availabilityModifiers.map((availability) =>
        this._unitOptionAvailabilityMapper.map(item.id, availability),
      ),
      useAllActivators: item.use_all_activators,
      useAllDesactivators: item.use_all_desactivators,
      hideNotActivated: item.hide_not_activated,
      isDefaultInList: item.is_default_in_list,
      changeProfil: item.change_profil,
      changeProfils: item.unit_option_change_profils.map((item) =>
        this._unitOptionChangeProfilMapper.map(item),
      ),
      changeModelRules: item.unit_option_change_model_rules.map((item) =>
        this._unitOptionChangeModelRuleMapper.map(item),
      ),
      canBeSelected: [
        UnitOptionUsePoint.Cost,
        UnitOptionUsePoint.Free,
        UnitOptionUsePoint.UpTo,
      ].includes(usePoints),
      magicStrandardId: item.magic_standard_id,
      magicItemId: item.magic_item_id,
      constraints:
        item.constraints?.map((item) => this._constraintMapper.map(item)) ?? [],
      modifiers:
        item.modifiers?.map((item) =>
          this._constraintModifierMapper.map(item),
        ) ?? [],
    } satisfies UnitOption as UnitOption;
  }

  public mapCategory(category: unit_option_categories): UnitOptionCategory {
    switch (category) {
      case unit_option_categories.BSB:
        return UnitOptionCategory.BSB;
      case unit_option_categories.C:
        return UnitOptionCategory.C;
      case unit_option_categories.M:
        return UnitOptionCategory.M;
      case unit_option_categories.S:
        return UnitOptionCategory.S;
      case unit_option_categories.EQUIPMENT:
        return UnitOptionCategory.Equipment;
      case unit_option_categories.SPECIAL_RULE:
        return UnitOptionCategory.SpecialRule;
      case unit_option_categories.EXTRA_SPELLS:
        return UnitOptionCategory.ExtraSpells;
      case unit_option_categories.GENERAL:
        return UnitOptionCategory.General;
      case unit_option_categories.MAGIC_BANNER:
        return UnitOptionCategory.MagicBanner;
      case unit_option_categories.MAGIC_EQUIPMENT:
        return UnitOptionCategory.MagicEquipment;
      case unit_option_categories.WIZARD_MASTER:
        return UnitOptionCategory.WizardMaster;
      case unit_option_categories.WIZARD_APPRENTICE:
        return UnitOptionCategory.WizardApprentice;
      case unit_option_categories.WIZARD_ADEPT:
        return UnitOptionCategory.WizardAdept;
      case unit_option_categories.ONE_CHOICE:
        return UnitOptionCategory.OneChoice;
      case unit_option_categories.MUST_CHOICE:
        return UnitOptionCategory.MustChoice;
      case unit_option_categories.MAY_TAKE:
        return UnitOptionCategory.MayTake;
      case unit_option_categories.MOUNT:
        return UnitOptionCategory.Mount;
      case unit_option_categories.MAGIC_PATH:
        return UnitOptionCategory.MagicPath;
      case unit_option_categories.MAGIC_LVL:
        return UnitOptionCategory.MagicLvl;
      case unit_option_categories.SPELL:
        return UnitOptionCategory.Spell;
      case unit_option_categories.MAGIC_ITEM:
        return UnitOptionCategory.MagicItem;
      case unit_option_categories.MAGIC_ITEM_CATEGORY:
        return UnitOptionCategory.MagicItemCategory;
      case unit_option_categories.MAGIC_STANDARD:
        return UnitOptionCategory.MagicStandard;
      case unit_option_categories.UNIT_EQUIPMENT:
        return UnitOptionCategory.UnitEquipment;
    }

    return UnitOptionCategory.Generic;
  }
}
